import { publicEnv } from '@/lib/config/public-env';
/**
 * The one builder for `/dani?prompt=` links that arrive from outside the app.
 *
 * Lifted out of `insight-engine/email-links.ts` (M4S-5583) when trial
 * onboarding became a second emitter. Two emitters copying a URL shape is how
 * the caps drift: this repo already carries two different prompt limits — 300
 * here, and 1000 in `useDaniDeepLinkPrompt`, which is deliberately looser
 * because it guards hand-crafted URLs rather than links we author. A third copy
 * would have been the one that silently disagreed.
 *
 * THE INVARIANT THAT MATTERS (OB-9): a link built here never carries `seed`.
 * `/dani` has two independent entry params, and `src/app/(private)/dani/page.tsx`
 * returns `null` when `seed` is present — so `ChatLanding` never mounts,
 * `useDaniDeepLinkPrompt` never runs, and a URL carrying both silently drops
 * `prompt` with no error anywhere. Reproduced against a live dev server on
 * 2026-09-13. Nothing in the product emits both today (every `seed` emitter is
 * an in-app `router.push` carrying `seed` alone), and building every external
 * link through here is what keeps that true.
 */

/**
 * The prompt rides in the URL, so keep it short enough to survive email-client
 * link munging and never let it carry PII (account names, recipient details).
 */
export const DANI_PROMPT_MAX_LENGTH = 300;

export const DANI_LANDING_PATH = '/dani';

/** `utm_campaign` values. One per outbound programme that deep-links into DANI. */
export type DaniLinkCampaign = 'engagement' | 'prospecting' | 'trial-onboarding';

export interface DaniDeepLinkParts {
    /**
     * The question to auto-submit on arrival. `null` builds a bare landing link
     * — used by CTAs whose copy is not question-shaped, so the recipient lands
     * in DANI ready to type rather than on a question nobody wrote.
     */
    prompt?: string | null;
    source: string;
    campaign: DaniLinkCampaign;
    content: string;
}

/**
 * Base origin for links embedded in email HTML. Prefers the deploy-time env
 * var so a dev machine never silently leaks localhost into a copied email;
 * window.location.origin is the dev fallback (email HTML is built client-side).
 */
export function resolveEmailBaseUrl(): string {
    const base =
        publicEnv('NEXT_PUBLIC_STUDIO_URL') ||
        (typeof window !== 'undefined' ? window.location.origin : '');
    return base.replace(/\/+$/, '');
}

/** Truncate to the URL-safe cap, marking the cut so a clipped prompt reads as clipped. */
export function capDaniPrompt(prompt: string): string {
    return prompt.length > DANI_PROMPT_MAX_LENGTH
        ? `${prompt.slice(0, DANI_PROMPT_MAX_LENGTH - 1)}…`
        : prompt;
}

/**
 * Path-only form: `/dani?prompt=…&utm_*`. Callers prepend the base URL so the
 * same builder serves absolute email links and in-app relative hrefs.
 */
export function daniDeepLinkPath({ prompt, source, campaign, content }: DaniDeepLinkParts): string {
    const params = new URLSearchParams();
    // Prompt first so it reads first in a pasted URL; URLSearchParams preserves
    // insertion order.
    if (prompt) params.set('prompt', capDaniPrompt(prompt));
    params.set('utm_source', source);
    params.set('utm_medium', 'email');
    params.set('utm_campaign', campaign);
    params.set('utm_content', content);
    return `${DANI_LANDING_PATH}?${params.toString()}`;
}

export const ONBOARDING_UTM_SOURCE = 'onboarding';
export const ONBOARDING_UTM_CAMPAIGN: DaniLinkCampaign = 'trial-onboarding';

/** Which of the four onboarding sends a link belongs to. */
export type OnboardingEmailNumber = 1 | 2 | 3 | 4;

/**
 * A CTA link for one of the four trial-onboarding emails (M4S-5566).
 *
 * `slot` distinguishes multiple links within one send — email 1 carries three
 * starter queries plus an "ask your own" button — so click-through is
 * attributable per link rather than per email.
 */
export function buildOnboardingDeepLink(
    emailNumber: OnboardingEmailNumber,
    prompt: string | null,
    slot?: string,
    baseUrl: string = resolveEmailBaseUrl(),
): string {
    const content = slot ? `email-${emailNumber}-${slot}` : `email-${emailNumber}`;
    // `resolveEmailBaseUrl` normalises its own result, but an explicitly passed
    // base does not — and a trailing slash here yields `//dani?…`, which some
    // clients resolve as protocol-relative. Normalise both paths through here.
    return `${baseUrl.replace(/\/+$/, '')}${daniDeepLinkPath({
        prompt,
        source: ONBOARDING_UTM_SOURCE,
        campaign: ONBOARDING_UTM_CAMPAIGN,
        content,
    })}`;
}
